import {
  Card,
  CardActionArea,
  Chip,
  Divider,
  Grid,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { TFunction } from 'i18next';
import React, { FC } from 'react';
import { NewUserType } from 'types/new-user';

const useStyles = makeStyles((theme: any) => ({
  card: {
    height: '100%',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: theme.palette.border.card,
  },
  actionArea: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    justifyContent: 'flex-start',
    padding: theme.spacing(2),
  },
  title: {
    color: theme.palette.tab.offselected,
    overflowWrap: 'anywhere',
    [theme.breakpoints.down('sm')]: { fontSize: theme.typography.body1.fontSize },
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.5),
  },
  chipAlt: { color: theme.palette.common.white },
  divider: {
    marginTop: theme.spacing(2),
    backgroundColor: theme.palette.border.card,
  },
  details: { marginTop: theme.spacing(2) },
  section: { '& + &': { marginTop: theme.spacing(1) } },
  subtitle: {
    display: 'block',
    textTransform: 'uppercase',
    color: theme.palette.text.shadow,
  },
  text: {
    color: theme.palette.text.primary,
    overflowWrap: 'anywhere',
  },
}));

type GridProps = {
  t: TFunction;
  parties: NewUserType[];
  handleEditNav: (id: string) => void;
};

type CardSection = { key: string; label: string; values: { id: string; text: string | number | null }[] };

// Section inclusion is data-driven; the card markup only renders the list.
const getCardSections = (t: TFunction, party: NewUserType): CardSection[] => [
  {
    key: 'addresses',
    label: t('users:addresses'),
    values: (party.addresses || []).map((item) => ({ id: item._id, text: item.address })),
  },
  {
    key: 'emails',
    label: t('users:emails'),
    values: (party.emails || []).map((item) => ({ id: item._id, text: item.address })),
  },
  {
    key: 'phones',
    label: t('users:phones'),
    values: (party.phones || []).map((item) => ({ id: item._id, text: item.number })),
  },
  {
    key: 'licenses',
    label: t('users:licenses'),
    values: (party.licenses || []).map((item) => ({ id: item._id, text: item.number })),
  },
].filter((section) => section.values.length > 0);

const GridResults: FC<GridProps> = ({ t, parties, handleEditNav }) => {
  const classes = useStyles();

  return (
    <Grid container direction="row" spacing={2}>
      {parties.map((party) => {
        const sections = getCardSections(t, party);

        return (
          <Grid key={party._id} item xs={12} sm={6} lg={4}>
            <Card elevation={0} className={classes.card}>
              <CardActionArea
                className={classes.actionArea}
                onClick={() => handleEditNav(party._id)}
              >
                <Typography variant="h6" component="h3" className={classes.title}>
                  {party.name}
                </Typography>

                <div className={classes.chips}>
                  <Chip size="small" color="primary" label={party.kind} />
                  {!!party.username?.trim() && (
                    <Chip
                      size="small"
                      color="secondary"
                      label={t('users:user-system')}
                      className={classes.chipAlt}
                    />
                  )}
                </div>

                <Divider className={classes.divider} />

                <div className={classes.details}>
                  {!sections.length && (
                    <Typography variant="body2" className={classes.text}>
                      {t('users:no-matching-data')}
                    </Typography>
                  )}

                  {sections.map((section) => (
                    <div key={section.key} className={classes.section}>
                      <Typography variant="caption" className={classes.subtitle}>
                        {section.label}
                      </Typography>
                      {section.values.map((value) => (
                        <Typography key={value.id} variant="body2" className={classes.text}>
                          {value.text}
                        </Typography>
                      ))}
                    </div>
                  ))}
                </div>
              </CardActionArea>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default GridResults;
